import React, { useState, useEffect } from "react";
import {
  Card,
  Table,
  Button,
  Tag,
  Space,
  Typography,
  Alert,
  Tooltip,
  Descriptions,
  Modal,
  message,
  Row,
  Col,
  Divider,
} from "antd";
import {
  DownloadOutlined,
  EyeOutlined,
  CarOutlined as TrackingOutlined,
  InboxOutlined as PackageOutlined,
  InfoCircleOutlined,
  ReloadOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import {
  SendcloudShipment,
  SendcloudTrackingEvent,
} from "../../types/sendcloud";
import { supabase } from "../../lib/supabase";
import { sendcloudService } from "../../services/sendcloudService";

const { Text, Link } = Typography;

interface SendcloudShipmentsProps {
  bookingId: number;
  onRefresh?: () => void;
}

interface ShipmentWithTracking extends SendcloudShipment {
  tracking_events?: SendcloudTrackingEvent[];
}

export const SendcloudShipments: React.FC<SendcloudShipmentsProps> = ({
  bookingId,
  onRefresh,
}) => {
  const [shipments, setShipments] = useState<ShipmentWithTracking[]>([]);
  const [loading, setLoading] = useState(false);
  const [detailsModalVisible, setDetailsModalVisible] = useState(false);
  const [selectedShipment, setSelectedShipment] =
    useState<ShipmentWithTracking | null>(null);
  const [trackingModalVisible, setTrackingModalVisible] = useState(false);

  useEffect(() => {
    fetchShipments();
  }, [bookingId]);

  const fetchShipments = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("sendcloud_shipments")
        .select(
          `
          *,
          sendcloud_tracking_events(*)
        `
        )
        .eq("booking_id", bookingId)
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Error fetching shipments:", error);
        message.error("Failed to fetch shipments");
        return;
      }

      const shipmentsWithTracking = (data || []).map(
        (
          shipment: SendcloudShipment & {
            sendcloud_tracking_events?: SendcloudTrackingEvent[];
          }
        ) => ({
          ...shipment,
          tracking_events: shipment.sendcloud_tracking_events || [],
        })
      );

      setShipments(shipmentsWithTracking);
    } catch (error) {
      console.error("Error fetching shipments:", error);
      message.error("Failed to fetch shipments");
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadLabel = async (shipment: SendcloudShipment) => {
    try {
      // Use the sendcloud_shipment_id to download via our API with proper authentication
      if (shipment.sendcloud_shipment_id) {
        message.loading("Downloading label...", 0.5);

        const result = await sendcloudService.downloadLabel({
          shipmentId: parseInt(shipment.sendcloud_shipment_id),
        });

        if (result.success && result.data) {
          const labelData = result.data as {
            label_url?: string;
            label_data?: string;
            content_type?: string;
          };

          if (labelData.label_data) {
            // Handle base64 PDF data
            try {
              const binaryString = atob(labelData.label_data);
              const bytes = new Uint8Array(binaryString.length);
              for (let i = 0; i < binaryString.length; i++) {
                bytes[i] = binaryString.charCodeAt(i);
              }

              const blob = new Blob([bytes], {
                type: labelData.content_type || "application/pdf",
              });
              const url = window.URL.createObjectURL(blob);
              window.open(url, "_blank");
              message.success("Label downloaded successfully");

              // Clean up the URL after a delay
              setTimeout(() => window.URL.revokeObjectURL(url), 1000);
            } catch (error) {
              console.error("Error processing label data:", error);
              message.error("Failed to process label data");
            }
          } else if (labelData.label_url) {
            window.open(labelData.label_url, "_blank");
            message.success("Label downloaded successfully");
          } else {
            message.warning("No label data available");
          }
        } else {
          message.error(result.error || "Failed to download label");
        }
      } else {
        message.warning("No shipment ID available for this shipment");
      }
    } catch (error) {
      console.error("Error downloading label:", error);
      message.error("Failed to download label");
    }
  };

  const handleTrackShipment = (shipment: SendcloudShipment) => {
    if (shipment.tracking_url) {
      window.open(shipment.tracking_url, "_blank");
    } else if (shipment.tracking_number) {
      // Generic tracking URL if no specific URL provided
      message.info(`Tracking number: ${shipment.tracking_number}`);
    } else {
      message.warning("No tracking information available");
    }
  };

  const showShipmentDetails = (shipment: ShipmentWithTracking) => {
    setSelectedShipment(shipment);
    setDetailsModalVisible(true);
  };

  const showTrackingDetails = (shipment: ShipmentWithTracking) => {
    setSelectedShipment(shipment);
    setTrackingModalVisible(true);
  };

  const handleBulkDownloadLabels = async () => {
    try {
      const shipmentsWithLabels = shipments.filter(
        (s) => s.label_url || s.sendcloud_shipment_id
      );

      if (shipmentsWithLabels.length === 0) {
        message.warning("No labels available for download");
        return;
      }

      message.loading(`Downloading ${shipmentsWithLabels.length} labels...`, 1);

      // Download each label
      for (const shipment of shipmentsWithLabels) {
        try {
          await handleDownloadLabel(shipment);
          // Small delay to prevent overwhelming the browser
          await new Promise((resolve) => setTimeout(resolve, 500));
        } catch (error) {
          console.error(
            `Failed to download label for shipment ${shipment.id}:`,
            error
          );
        }
      }

      message.success(`${shipmentsWithLabels.length} labels downloaded`);
    } catch (error) {
      console.error("Error in bulk download:", error);
      message.error("Failed to download some labels");
    }
  };

  const checkRelatedShipments = async (shipment: SendcloudShipment) => {
    // Check if this shipment has related multi-collo shipments
    const multicolloId =
      (shipment.sendcloud_data as any)?.multicollo_main_shipment_id ||
      (shipment.sendcloud_data as any)?.colli_uuid ||
      (shipment.sendcloud_data as any)?.shipment_uuid;

    if (!multicolloId) {
      return [];
    }

    try {
      // Find related shipments with the same multi-collo ID
      const relatedShipments = shipments.filter(
        (s) =>
          s.id !== shipment.id &&
          s.status !== "cancelled" &&
          ((s.sendcloud_data as any)?.multicollo_main_shipment_id ===
            multicolloId ||
            (s.sendcloud_data as any)?.colli_uuid === multicolloId ||
            (s.sendcloud_data as any)?.shipment_uuid === multicolloId)
      );

      return relatedShipments;
    } catch (error) {
      console.error("Error checking related shipments:", error);
      return [];
    }
  };

  const handleDeleteShipment = async (shipment: SendcloudShipment) => {
    // Check for related multi-collo shipments
    const relatedShipments = await checkRelatedShipments(shipment);

    // First modal: Ask if they want to cancel in Sendcloud
    Modal.confirm({
      title: "Cancel in Sendcloud?",
      content: (
        <div>
          <p>Do you want to cancel this shipment in Sendcloud as well?</p>
          <p>
            <strong>Tracking Number:</strong>{" "}
            {shipment.tracking_number || "N/A"}
          </p>
          <p>
            <strong>Destination:</strong> {shipment.to_name}, {shipment.to_city}
          </p>

          {relatedShipments.length > 0 && (
            <div
              style={{
                marginTop: "12px",
                padding: "8px",
                backgroundColor: "#fff7e6",
                borderRadius: "4px",
                border: "1px solid #ffc53d",
              }}
            >
              <p
                style={{
                  margin: "0 0 8px 0",
                  fontWeight: "bold",
                  color: "#d46b08",
                }}
              >
                🔗 Multi-collo Shipment Detected
              </p>
              <p style={{ margin: "0 0 8px 0", fontSize: "12px" }}>
                This shipment is part of a multi-collo group. The following
                related shipments will also be affected:
              </p>
              {relatedShipments.slice(0, 3).map((related, index) => (
                <div
                  key={related.id}
                  style={{ fontSize: "11px", marginLeft: "8px" }}
                >
                  • {related.tracking_number || related.sendcloud_shipment_id} →{" "}
                  {related.to_name}
                </div>
              ))}
              {relatedShipments.length > 3 && (
                <div
                  style={{
                    fontSize: "11px",
                    marginLeft: "8px",
                    fontStyle: "italic",
                  }}
                >
                  ... and {relatedShipments.length - 3} more shipments
                </div>
              )}
              <p
                style={{
                  margin: "8px 0 0 0",
                  fontSize: "11px",
                  color: "#8c8c8c",
                }}
              >
                All {relatedShipments.length + 1} shipments in this group will
                be cancelled/deleted.
              </p>
            </div>
          )}

          <p style={{ color: "#1890ff", fontSize: "12px", marginTop: "8px" }}>
            ℹ️ If the shipment is already being processed by the carrier,
            cancellation in Sendcloud may not be possible.
          </p>
        </div>
      ),
      okText: "Yes, Cancel in Sendcloud",
      cancelText: "No, Database Only",
      onOk: async () => {
        // Confirm deletion with Sendcloud cancellation
        confirmDelete(shipment, true, relatedShipments);
      },
      onCancel: () => {
        // Confirm deletion without Sendcloud cancellation
        confirmDelete(shipment, false, relatedShipments);
      },
    });
  };

  const confirmDelete = (
    shipment: SendcloudShipment,
    cancelInSendcloud: boolean,
    relatedShipments: SendcloudShipment[] = []
  ) => {
    Modal.confirm({
      title: `Delete ${
        relatedShipments.length > 0 ? "Multi-collo " : ""
      }Shipment${relatedShipments.length > 0 ? "s" : ""}`,
      content: (
        <div>
          <p>
            Are you sure you want to delete{" "}
            {relatedShipments.length > 0 ? "these shipments" : "this shipment"}?
          </p>
          <p>
            <strong>Action:</strong>{" "}
            {cancelInSendcloud
              ? "Cancel in Sendcloud AND remove from database"
              : "Remove from database only"}
          </p>

          {relatedShipments.length > 0 && (
            <div
              style={{
                marginTop: "12px",
                padding: "8px",
                backgroundColor: "#fff2f0",
                borderRadius: "4px",
                border: "1px solid #ffccc7",
              }}
            >
              <p
                style={{
                  margin: "0 0 8px 0",
                  fontWeight: "bold",
                  color: "#cf1322",
                }}
              >
                📦 Shipments to be deleted:
              </p>
              <div style={{ fontSize: "11px", marginLeft: "8px" }}>
                • <strong>Main:</strong>{" "}
                {shipment.tracking_number || shipment.sendcloud_shipment_id} →{" "}
                {shipment.to_name}
              </div>
              {relatedShipments.slice(0, 3).map((related, index) => (
                <div
                  key={related.id}
                  style={{ fontSize: "11px", marginLeft: "8px" }}
                >
                  • <strong>Related:</strong>{" "}
                  {related.tracking_number || related.sendcloud_shipment_id} →{" "}
                  {related.to_name}
                </div>
              ))}
              {relatedShipments.length > 3 && (
                <div
                  style={{
                    fontSize: "11px",
                    marginLeft: "8px",
                    fontStyle: "italic",
                  }}
                >
                  ... and {relatedShipments.length - 3} more related shipments
                </div>
              )}
              <p
                style={{
                  margin: "8px 0 0 0",
                  fontSize: "11px",
                  color: "#8c8c8c",
                }}
              >
                Total: {relatedShipments.length + 1} shipments will be deleted
              </p>
            </div>
          )}

          <p style={{ color: "#ff4d4f", fontSize: "12px", marginTop: "8px" }}>
            ⚠️ This action cannot be undone.
          </p>
        </div>
      ),
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          const relatedShipmentIds = relatedShipments.map((s) => s.id);
          const response = await sendcloudService.deleteShipment(
            shipment.id,
            cancelInSendcloud,
            relatedShipmentIds
          );

          if (response.success) {
            // Use the actual counts from the edge function response
            const isMultiCollo = response.data?.multi_collo_detected;
            const totalProcessed =
              response.data?.total_shipments_processed || 1;
            const forceCancelledCount =
              response.data?.force_cancelled_count || 0;

            let successMessage = cancelInSendcloud
              ? `${totalProcessed} shipment${
                  totalProcessed > 1 ? "s" : ""
                } cancelled in Sendcloud and deleted from database`
              : `${totalProcessed} shipment${
                  totalProcessed > 1 ? "s" : ""
                } deleted from database`;

            if (isMultiCollo) {
              successMessage += ` (multi-collo group processed)`;
            }

            if (
              response.data?.partial_success &&
              response.data?.errors?.length > 0
            ) {
              const alreadyCancelledCount = response.data.errors.filter(
                (error: string) =>
                  error.includes("already being cancelled") ||
                  error.includes("already cancelled")
              ).length;

              if (alreadyCancelledCount > 0) {
                successMessage += ` - Some parcels were already cancelled`;
              }
            }

            if (forceCancelledCount > 0) {
              successMessage += ` (${forceCancelledCount} force-cancelled)`;
            }

            message.success(successMessage);

            // Refresh the shipments list
            fetchShipments();
            onRefresh?.();
          } else {
            message.error(response.error || "Failed to delete shipment");
          }
        } catch (error) {
          console.error("Error deleting shipment:", error);
          message.error("Failed to delete shipment");
        }
      },
    });
  };

  const getStatusColor = (status: string) => {
    const statusColors: Record<string, string> = {
      created: "blue",
      pending: "orange",
      ready_to_send: "cyan",
      sent_to_carrier: "purple",
      in_transit: "geekblue",
      delivered: "green",
      exception: "red",
      cancelled: "red",
    };
    return statusColors[status.toLowerCase()] || "default";
  };

  const formatWeight = (weightInGrams: number | null) => {
    if (!weightInGrams) return "N/A";
    if (weightInGrams >= 1000) {
      return `${(weightInGrams / 1000).toFixed(2)} kg`;
    }
    return `${weightInGrams} g`;
  };

  const formatDimensions = (
    length: number | null,
    width: number | null,
    height: number | null
  ) => {
    if (!length || !width || !height) return "N/A";
    return `${length}×${width}×${height} cm`;
  };

  const columns = [
    {
      title: "Shipment ID",
      dataIndex: "sendcloud_shipment_id",
      key: "sendcloud_shipment_id",
      width: 120,
      render: (id: string) => (
        <Text code style={{ fontSize: "11px" }}>
          {id}
        </Text>
      ),
    },
    {
      title: "Tracking",
      key: "tracking",
      width: 200,
      render: (record: ShipmentWithTracking) => (
        <div>
          {record.tracking_number ? (
            <div>
              <Text strong style={{ fontSize: "12px" }}>
                {record.tracking_number}
              </Text>
              <br />
              <Text type="secondary" style={{ fontSize: "10px" }}>
                {record.carrier_name || "Unknown Carrier"}
              </Text>
            </div>
          ) : (
            <Text type="secondary" style={{ fontSize: "11px" }}>
              No tracking yet
            </Text>
          )}
        </div>
      ),
    },
    {
      title: "Destination",
      key: "destination",
      width: 180,
      render: (record: ShipmentWithTracking) => (
        <div>
          <Text strong style={{ fontSize: "11px" }}>
            {record.to_name}
          </Text>
          {record.to_company_name && (
            <>
              <br />
              <Text style={{ fontSize: "10px" }}>{record.to_company_name}</Text>
            </>
          )}
          <br />
          <Text type="secondary" style={{ fontSize: "10px" }}>
            {record.to_city}, {record.to_country}
          </Text>
        </div>
      ),
    },
    {
      title: "Package",
      key: "package",
      width: 120,
      render: (record: ShipmentWithTracking) => (
        <div>
          <Text style={{ fontSize: "11px" }}>
            {formatWeight(record.weight)}
          </Text>
          <br />
          <Text type="secondary" style={{ fontSize: "10px" }}>
            {formatDimensions(record.length, record.width, record.height)}
          </Text>
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 100,
      render: (status: string) => (
        <Tag color={getStatusColor(status)} style={{ fontSize: "10px" }}>
          {status.replace(/_/g, " ").toUpperCase()}
        </Tag>
      ),
    },
    {
      title: "Created",
      dataIndex: "created_at",
      key: "created_at",
      width: 140,
      render: (date: string) => (
        <div>
          <Text style={{ fontSize: "10px", display: "block" }}>
            {new Date(date).toLocaleDateString()}
          </Text>
          <Text type="secondary" style={{ fontSize: "9px" }}>
            {new Date(date).toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
              hour12: false,
            })}
          </Text>
        </div>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 200,
      render: (record: ShipmentWithTracking) => (
        <Space size="small">
          <Tooltip title="View Details">
            <Button
              type="text"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => showShipmentDetails(record)}
            />
          </Tooltip>

          {record.tracking_number && (
            <Tooltip title="Track Shipment">
              <Button
                type="text"
                size="small"
                icon={<TrackingOutlined />}
                onClick={() => handleTrackShipment(record)}
              />
            </Tooltip>
          )}

          {record.tracking_events && record.tracking_events.length > 0 && (
            <Tooltip title="Tracking History">
              <Button
                type="text"
                size="small"
                icon={<InfoCircleOutlined />}
                onClick={() => showTrackingDetails(record)}
              />
            </Tooltip>
          )}

          {record.label_url && (
            <Tooltip title="Download Label">
              <Button
                type="text"
                size="small"
                icon={<DownloadOutlined />}
                onClick={() => handleDownloadLabel(record)}
              />
            </Tooltip>
          )}

          <Tooltip title="Delete Shipment">
            <Button
              type="text"
              size="small"
              icon={<DeleteOutlined />}
              danger
              onClick={() => handleDeleteShipment(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  if (shipments.length === 0 && !loading) {
    return (
      <Alert
        type="info"
        showIcon
        message="No Shipments Created"
        description="No shipments have been created for this booking yet. Use the shipping options above to create a shipment."
        style={{ marginTop: 16 }}
      />
    );
  }

  return (
    <div style={{ marginTop: 16 }}>
      <Card
        title={
          <Space>
            <PackageOutlined />
            <span>Sendcloud Shipments ({shipments.length})</span>
          </Space>
        }
        size="small"
        extra={
          <Space>
            {shipments.length > 0 && (
              <Button
                type="text"
                size="small"
                icon={<DownloadOutlined />}
                onClick={handleBulkDownloadLabels}
                disabled={
                  shipments.filter(
                    (s) => s.label_url || s.sendcloud_shipment_id
                  ).length === 0
                }
              >
                Download All Labels
              </Button>
            )}
            <Button
              type="text"
              size="small"
              icon={<ReloadOutlined />}
              onClick={() => {
                fetchShipments();
                onRefresh?.();
              }}
              loading={loading}
            >
              Refresh
            </Button>
          </Space>
        }
      >
        <Table
          dataSource={shipments}
          columns={columns}
          rowKey="id"
          size="small"
          pagination={false}
          loading={loading}
          scroll={{ x: true }}
        />
      </Card>

      {/* Shipment Details Modal */}
      <Modal
        title={
          <Space>
            <PackageOutlined />
            <span>Shipment Details</span>
          </Space>
        }
        open={detailsModalVisible}
        onCancel={() => setDetailsModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailsModalVisible(false)}>
            Close
          </Button>,
          selectedShipment?.label_url && (
            <Button
              key="download"
              type="primary"
              icon={<DownloadOutlined />}
              onClick={() =>
                selectedShipment && handleDownloadLabel(selectedShipment)
              }
            >
              Download Label
            </Button>
          ),
        ].filter(Boolean)}
        width={800}
      >
        {selectedShipment && (
          <div>
            <Row gutter={16}>
              <Col span={12}>
                <Descriptions
                  title="Shipment Information"
                  size="small"
                  column={1}
                >
                  <Descriptions.Item label="Sendcloud ID">
                    <Text code>{selectedShipment.sendcloud_shipment_id}</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label="Tracking Number">
                    {selectedShipment.tracking_number ? (
                      <Space>
                        <Text code>{selectedShipment.tracking_number}</Text>
                        {selectedShipment.tracking_url && (
                          <Link
                            href={selectedShipment.tracking_url}
                            target="_blank"
                            style={{ fontSize: "12px" }}
                          >
                            Track Online
                          </Link>
                        )}
                      </Space>
                    ) : (
                      <Text type="secondary">Not available</Text>
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label="Carrier">
                    {selectedShipment.carrier_name || "Unknown"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Shipping Method">
                    {selectedShipment.shipping_method || "Unknown"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Status">
                    <Tag color={getStatusColor(selectedShipment.status)}>
                      {selectedShipment.status.replace(/_/g, " ").toUpperCase()}
                    </Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="Created">
                    {new Date(selectedShipment.created_at).toLocaleString()}
                  </Descriptions.Item>
                </Descriptions>
              </Col>

              <Col span={12}>
                <Descriptions title="Package Details" size="small" column={1}>
                  <Descriptions.Item label="Weight">
                    {formatWeight(selectedShipment.weight)}
                  </Descriptions.Item>
                  <Descriptions.Item label="Dimensions">
                    {formatDimensions(
                      selectedShipment.length,
                      selectedShipment.width,
                      selectedShipment.height
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label="Currency">
                    {selectedShipment.currency || "EUR"}
                  </Descriptions.Item>
                </Descriptions>
              </Col>
            </Row>

            <Divider />

            <Descriptions title="Delivery Address" size="small" column={2}>
              <Descriptions.Item label="Name">
                {selectedShipment.to_name}
              </Descriptions.Item>
              <Descriptions.Item label="Company">
                {selectedShipment.to_company_name || "N/A"}
              </Descriptions.Item>
              <Descriptions.Item label="Address">
                {selectedShipment.to_street_name}{" "}
                {selectedShipment.to_house_number}
              </Descriptions.Item>
              <Descriptions.Item label="City">
                {selectedShipment.to_city}
              </Descriptions.Item>
              <Descriptions.Item label="Postal Code">
                {selectedShipment.to_postal_code}
              </Descriptions.Item>
              <Descriptions.Item label="Country">
                {selectedShipment.to_country}
              </Descriptions.Item>
              <Descriptions.Item label="Email">
                {selectedShipment.to_email || "N/A"}
              </Descriptions.Item>
              <Descriptions.Item label="Phone">
                {selectedShipment.to_telephone || "N/A"}
              </Descriptions.Item>
            </Descriptions>

            {selectedShipment.label_url && (
              <>
                <Divider />
                <Alert
                  type="success"
                  showIcon
                  message="Shipping Label Available"
                  description={
                    <Space>
                      <span>The shipping label is ready for download.</span>
                      <Link
                        href={selectedShipment.label_url}
                        target="_blank"
                        style={{ fontSize: "12px" }}
                      >
                        Open in New Tab
                      </Link>
                    </Space>
                  }
                />
              </>
            )}
          </div>
        )}
      </Modal>

      {/* Tracking Events Modal */}
      <Modal
        title={
          <Space>
            <TrackingOutlined />
            <span>Tracking History</span>
          </Space>
        }
        open={trackingModalVisible}
        onCancel={() => setTrackingModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setTrackingModalVisible(false)}>
            Close
          </Button>,
        ]}
        width={600}
      >
        {selectedShipment && (
          <div>
            {selectedShipment.tracking_events &&
            selectedShipment.tracking_events.length > 0 ? (
              <Table
                dataSource={selectedShipment.tracking_events}
                size="small"
                pagination={false}
                columns={[
                  {
                    title: "Date",
                    dataIndex: "event_timestamp",
                    key: "event_timestamp",
                    width: 120,
                    render: (date: string) =>
                      new Date(date).toLocaleDateString(),
                  },
                  {
                    title: "Status",
                    dataIndex: "status",
                    key: "status",
                    width: 120,
                    render: (status: string) => (
                      <Tag color={getStatusColor(status)}>
                        {status.replace(/_/g, " ").toUpperCase()}
                      </Tag>
                    ),
                  },
                  {
                    title: "Description",
                    dataIndex: "description",
                    key: "description",
                  },
                  {
                    title: "Location",
                    dataIndex: "location",
                    key: "location",
                    width: 100,
                  },
                ]}
              />
            ) : (
              <Alert
                type="info"
                message="No tracking events available"
                description="Tracking information will appear here once the shipment is processed by the carrier."
              />
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default SendcloudShipments;
